import React, { useRef } from "react";
import { Form } from "react-bootstrap";
import { FormikProps } from "formik";

interface FileUploadInputProps {
  label: string;
  name: string;
  required?: boolean;
  formik: FormikProps<any>;
}

const NewFileUploadInput: React.FC<FileUploadInputProps> = ({
  label,
  name,
  required = false,
  formik,
}) => {
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const file: File | string | null = formik.values[name] ?? null;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      formik.setFieldValue(name, selectedFile);
    }
  };

  const handleRemove = () => {
    formik.setFieldValue(name, null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const triggerFileDialog = () => {
    fileInputRef.current?.click();
  };

  /** 🔍 Preview handler */
  const handlePreview = () => {
    if (!file) return;

    const url =
      typeof file === "string" ? file : URL.createObjectURL(file);

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const fileName =
    typeof file === "string"
      ? file.split("/").pop()
      : file?.name;

  const hasError =
    Boolean(formik.touched[name]) && Boolean(formik.errors[name]);

  return (
    <div className="mb-4">
      <Form.Label>
        {label} {required && <span className="text-danger">*</span>}
      </Form.Label>

      <div className="position-relative">
        {/* Hidden File Input */}
        <Form.Control
          ref={fileInputRef}
          type="file"
          name={name}
          onChange={handleFileChange}
          onBlur={formik.handleBlur}
          isInvalid={hasError}
          style={{ display: "none" }}
        />

        {/* Visible Input UI */}
        <div
          className="d-flex align-items-center border rounded px-3 py-2"
          style={{
            height: "40px",
            backgroundColor: "#f9f9f9",
            borderColor: hasError ? "#dc3545" : "#ced4da",
            cursor: "pointer",
          }}
          onClick={triggerFileDialog}
        >
          {/* File name / placeholder */}
          <span
            className="flex-grow-1 text-muted text-truncate"
            style={{
              fontSize: "13px",
              overflow: "hidden",
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
            }}
            title={fileName || "Upload Doc"}
          >
            {fileName || "Upload Doc"}
          </span>

          {/* Icons */}
          {file ? (
            <div className="d-flex align-items-center gap-3 ms-2">
              {/* 👁 Preview */}
              <i
                className="bi bi-eye"
                title="Preview"
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handlePreview();
                }}
              />

              {/* ❌ Remove */}
              <i
                className="bi bi-x-lg text-danger"
                title="Remove"
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  handleRemove();
                }}
              />
            </div>
          ) : (
            /* ⬆ Upload */
            <i
              className="bi bi-upload ms-2"
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                triggerFileDialog();
              }}
            />
          )}
        </div>

        {/* Validation */}
        {hasError && (
          <Form.Control.Feedback type="invalid" className="d-block">
            {String(formik.errors[name])}
          </Form.Control.Feedback>
        )}
      </div>
    </div>
  );
};

export default NewFileUploadInput;
